import React, { Component, lazy, Suspense } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { isDirty, initialize } from 'redux-form';
import { injectIntl, IntlProvider } from 'react-intl';
import { displayMode } from 'common/forms/displayMode';
import { Redirect } from 'react-router-dom';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import ServerError from 'components/Errors/ServerError';
import NotFoundError from 'components/Errors/NotFoundError';
import CustomerAvatar from 'components/CustomerAvatar';
import Button from 'components/Common/Buttons';
import LocalisedMessage from 'components/Common/LocalisedMessage';
import ScrollToTop from 'components/ScrollToTop';
import ValidationResults from 'components/ValidationResults';
import ValidationResultsStacked from 'components/ValidationResultsStacked';
import FormConfirmation from 'components/Modals/FormConfirmation';
import FormSigning from 'components/Modals/FormSigning';
import PenaltiesOfPerjuryModal from 'components/Modals/PenaltiesOfPerjuryModal';
import { submissionStages } from 'common/forms/submissionStages';
import SupportingDocumentsModal from 'components/Modals/SupportingDocumentsModal';
import { isTranslatedForm } from 'common/forms/formTypeHelper';
import { getFormType } from 'common/forms/formTypes';
import { defaultLanguage } from 'common/language/localeHelper';

import {
  validate,
  getFormById,
  certifyForm,
  setSubmissionStage,
  confirmForm,
  submitForm,
  getPreviewPdf,
  clearDefaultForm,
  clearPdfDownload,
  getCollectableSupportingDocPdf,
  clearCollectableSupportingDocPdfDownload
} from 'actions';

import {
  LazyBlankBene2017,
  LazyBlankBene2021,
  LazyBlankBen,
  LazyBlankBen2021,
  LazyBlankW92018,
  LazyBlankW92024,
  LazyBlankEci,
  LazyBlankEci2021,
  LazyBlankExp2017,
  LazyBlankExp2023,
  LazyBlankImy2017,
  LazyBlankImy2021,
  LazyBlankCrsE,
  LazyBlankCrsCp,
  LazyBlankCrsI,
  LazyBlankCrsI2024
} from './lazyBlanks';

const FormQuestionnaire = lazy(() => import(/* webpackChunkName: "blank.questionnaires" */ 'components/Questionnaires/FormQuestionnaire'));

class FormContainer extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.shape({
      path: PropTypes.string,
      params: PropTypes.shape({
        formType: PropTypes.string,
        formId: PropTypes.string
      })
    }).isRequired,
    history: PropTypes.shape({
      go: PropTypes.func,
      block: PropTypes.func,
      push: PropTypes.func,
      location: PropTypes.shape({
        pathname: PropTypes.string
      })
    }).isRequired,
    isGettingForm: PropTypes.bool,
    formData: PropTypes.shape({
      isOnlineForm: PropTypes.bool,
      originalFileName: PropTypes.string
    }),
    isValidatingForm: PropTypes.bool,
    validationResult: PropTypes.shape({
      result: PropTypes.shape({}),
      rejections: PropTypes.oneOfType([
        PropTypes.shape({
          length: PropTypes.number
        }),
        PropTypes.arrayOf(PropTypes.shape({}))
      ]),
      valid: PropTypes.bool,
      customerId: PropTypes.string,
      customerName: PropTypes.string,
    }),
    supportingDocs: PropTypes.arrayOf(PropTypes.shape({})),
    workflowState: PropTypes.shape({
      stage: PropTypes.number
    }),
    instructionToggled: PropTypes.string,
    dirty: PropTypes.bool,
    reduxFormName: PropTypes.string,
    formError: PropTypes.shape({
      code: PropTypes.number,
      message: PropTypes.string
    }),
    submissionStage: PropTypes.string,
    pdfData: PropTypes.string,
    isFetchingPdfForm: PropTypes.bool,
    downloadRequestFormType: PropTypes.string,
    showSupportingDocs: PropTypes.bool,
    previewOnlyMode: PropTypes.bool,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired
    }).isRequired,
    formDisplayMode: PropTypes.number.isRequired,
    usePreQualifyingQuestionnaire: PropTypes.bool,
    customerName: PropTypes.string,
    authenticationType: PropTypes.number,
    authenticationPinVerified: PropTypes.bool,
    emailAddress: PropTypes.string,
    defaultFormType: PropTypes.string,
    formTypes: PropTypes.arrayOf(PropTypes.shape({})),
    locale: PropTypes.string.isRequired,
    messages: PropTypes.shape({}).isRequired,
    englishMessages: PropTypes.shape({}).isRequired,
    isFetchingCollectablePdf: PropTypes.bool,
    collectableSupportingDocPdfData: PropTypes.string,
    selectedAccountId: PropTypes.number,
    formQuestionnaire: PropTypes.shape({
      answers: PropTypes.shape({
        name: PropTypes.shape({
          name: PropTypes.string
        }),
        signer: PropTypes.shape({
          name: PropTypes.string
        })
      })
    })
  };

  static defaultProps = {
    isValidatingForm: false,
    validationResult: null,
    supportingDocs: null,
    workflowState: null,
    isGettingForm: true,
    formData: null,
    formError: null,
    instructionToggled: null,
    dirty: false,
    reduxFormName: null,
    submissionStage: submissionStages.Validating,
    pdfData: null,
    isFetchingPdfForm: false,
    downloadRequestFormType: null,
    showSupportingDocs: false,
    previewOnlyMode: false,
    usePreQualifyingQuestionnaire: false,
    customerName: null,
    authenticationType: null,
    authenticationPinVerified: false,
    emailAddress: null,
    defaultFormType: null,
    formTypes: null,
    isFetchingCollectablePdf: false,
    collectableSupportingDocPdfData: null,
    selectedAccountId: null,
    formQuestionnaire: null
  };

  static resizeTimer;

  static maxMobileWidth = 992;

  static windowWidth;

  static isMobileSize() {
    return FormContainer.windowWidth <= FormContainer.maxMobileWidth;
  }

  static updateFormDimensions() {
    FormContainer.windowWidth = window.innerWidth;

    if (FormContainer.isMobileSize()) {
      const contentElement = document.getElementById('form-content');
      if (contentElement != null) {
        document.getElementById('form-content').style.height = 'auto';
      }
      return;
    }
    if (FormContainer.resizeTimer) {
      clearInterval(FormContainer.resizeTimer);
      FormContainer.resizeTimer = null;
    }
    FormContainer.resizeTimer = setInterval(() => {
      FormContainer.adjustFormContainerHeight();
    }, 1000, this);
  }

  static adjustFormContainerHeight() {
    clearInterval(FormContainer.resizeTimer);
    FormContainer.resizeTimer = null;

    let height = 0;

    if (!document.getElementById('form-content')
    || !document.getElementById('form-content').firstChild
    || !document.getElementById('blankForm')) {
      return;
    }
    const pages = document.getElementById('form-content').firstChild.childNodes;

    if (!pages || pages.length === 0) {
      return;
    }

    let top = 0;
    let bottom = 0;
    for (let i = 0; i < pages.length; i += 1) {
      if (pages != null && pages.length > 1 && pages[i].className != null) {
        if (pages[i].className.includes('page')) {
          if (top === 0) {
            top = Math.ceil(pages[i].getBoundingClientRect().top);
          }
          bottom = Math.ceil(pages[i].getBoundingClientRect().bottom);
        }
      }
    }

    if (top === 0 || bottom === 0) {
      return;
    }

    height = bottom - top;

    document.getElementById('form-content').style.height = `${ height }px`;
  }

  constructor(props) {
    super(props);

    const { formDisplayMode } = this.props;

    this.state = {
      showMobileView: window.innerWidth <= FormContainer.maxMobileWidth,
      showScrollToTop: formDisplayMode !== displayMode.Stacked && window.scrollY !== 0,
      pdfDownload: null,
      showResult: false,
      collectablePdfDownload: null,
    };

    this.unblock = props.history.block((location, action) => {
      const { dirty } = this.props;
      if (dirty) {
        return 'Are you sure you want to leave this page?';
      }
      return null;
    });
    this.showScrollToTopIcon = this.showScrollToTopIcon.bind(this);
    this.adjustFormView = this.adjustFormView.bind(this);
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    const { params: { formType, formId } } = match;

    dispatch(getFormById(formType, formId));

    this.setMainContainerClass();

    window.addEventListener('scroll', this.showScrollToTopIcon, false);
    window.addEventListener('resize', this.adjustFormView, false);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      dispatch,
      match,
      instructionToggled,
      isFetchingPdfForm,
      isValidatingForm,
      isFetchingCollectablePdf
    } = this.props;

    if (match.path !== newProps.match.path && match.params !== newProps.match.params) {
      const { params: { formType, formId } } = newProps.match;
      dispatch(getFormById(formType, formId));
      return;
    }

    if (isFetchingPdfForm && !newProps.isFetchingPdfForm && newProps.pdfData) {
      this.setState({
        pdfDownload: newProps.pdfData
      });
      const link = document.createElement('a');
      link.href = newProps.pdfData;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', `${ match.params.formType }.pdf`);
      document.body.appendChild(link);
      link.click();
    } else {
      this.setState({
        pdfDownload: null
      });
    }

    if (isFetchingCollectablePdf && !newProps.isFetchingCollectablePdf && newProps.collectableSupportingDocPdfData) {
      this.setState({
        collectablePdfDownload: newProps.collectableSupportingDocPdfData
      });
      const link = document.createElement('a');
      link.href = newProps.collectableSupportingDocPdfData;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', 'affidavit.pdf');
      document.body.appendChild(link);
      link.click();
    } else {
      this.setState({
        collectablePdfDownload: null
      });
    }

    if (!newProps.isValidatingForm && isValidatingForm) {
      this.setState({ showResult: true });
    }

    if (newProps.instructionToggled && newProps.instructionToggled !== instructionToggled) {
      FormContainer.updateFormDimensions();
    }
  }

  componentDidUpdate(prevProps) {
    const { dispatch, isValidatingForm, workflowState, history, defaultFormType } = this.props;
    FormContainer.updateFormDimensions();

    if (!isValidatingForm && prevProps.isValidatingForm) {
      const element = document.getElementById('validation-results');
      if (element != null) {
        element.scrollIntoView();
      }
    }

    if (workflowState && workflowState.stage > 2) {
      if (defaultFormType) {
        dispatch(clearDefaultForm());
      }
      history.push(`${ history.location.pathname }/success`);
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    const { pdfDownload, collectablePdfDownload } = this.state;
    window.removeEventListener('resize', this.adjustFormView, false);
    window.removeEventListener('scroll', this.showScrollToTopIcon, false);

    this.unblock();

    const mainContainer = document.getElementById('main-container');
    if (mainContainer) {
      mainContainer.className = 'container main-content';
    }

    if (pdfDownload) {
      URL.revokeObjectURL(pdfDownload);
    }
    dispatch(clearPdfDownload());

    if (collectablePdfDownload) {
      URL.revokeObjectURL(collectablePdfDownload);
    }
    dispatch(clearCollectableSupportingDocPdfDownload());
  }

  onValidateForm(formType, values) {
    const { dispatch, match, reduxFormName } = this.props;
    const { params: { formId } } = match;

    dispatch(validate(formType, formId, values));
    dispatch(initialize(reduxFormName, values));
  }

  onFormConfirmationConfirm() {
    const { dispatch, match } = this.props;
    const { params: { formId, formType } } = match;
    dispatch(confirmForm(formType, formId));
  }

  onFormConfirmationCancel() {
    this.resetSubmisson();
  }

  onCertificationConfirm() {
    const { dispatch, match: { params: { formId } } } = this.props;
    dispatch(certifyForm(formId));
  }

  onCertificationCancel() {
    this.resetSubmisson();
  }

  onFormSigningCancel() {
    this.resetSubmisson();
  }

  onUploadDocumentClick() {
    this.setState({ showSupportingDocs: true });
  }

  getBlankComponent(BlankComponent) {
    const { match: { params: { formType } }, intl } = this.props;
    return (
      <React.Suspense fallback={ <div className="loading-container"><LoadingWrapper text={ intl.formatMessage({ id: 'pleaseWaitText' }) } /></div> }>
        <BlankComponent
          onSubmit={ values => this.onValidateForm(formType, values) }
          onValidationResultsClick={ () => this.setState({ showResult: true }) }
          onSupportingDocumentsClick={ () => this.setState({ showSupportingDocs: true }) }
        />
      </React.Suspense>
    );
  }

  getWrapperClasses() {
    const { match: { params: { formType } }, formDisplayMode } = this.props;
    const { showMobileView } = this.state;
    let classes = `${ showMobileView ? 'mobile-' : '' }blank-wrapper`;

    classes += formType && formType.indexOf('w') === 0 && formDisplayMode !== displayMode.Stacked ? ' w-blank-wrapper' : '';
    classes += formType && (formType.indexOf('crs') === 0 || formType.indexOf('ucrs') === 0) && formDisplayMode !== displayMode.Stacked ? ' crs-blank-wrapper' : '';
    classes += formType && formType.indexOf('rc') === 0 && formDisplayMode !== displayMode.Stacked ? ' rc-blank-wrapper' : '';
    classes += (navigator.userAgent.indexOf('Edge') < 0 && navigator.userAgent.indexOf('Edg') !== -1 && window.screenTop > 0) ? ' wrapper-fullscreen' : '';
    return classes;
  }

  getActionElements() {
    const { showMobileView, showScrollToTop } = this.state;
    const {
      isGettingForm,
      isValidatingForm,
      formError,
      workflowState,
      dirty,
      formDisplayMode
    } = this.props;
    const showStackedView = formDisplayMode === displayMode.Stacked && !showMobileView;
    const isBusy = isGettingForm || isValidatingForm;
    return (
      <React.Fragment>
        { !isGettingForm && formError == null && workflowState != null && <div className="form-action-bar">
          { showStackedView && <p>Please check your answers to resolve any problems before you can continue.</p>}
          { this.showSignAndSubmit() && !isBusy && <Button
            id="sign-submit-action-button"
            type="button"
            className={ `btn btn-primary btn-inline${ isValidatingForm ? ' btn-active' : '' } mt-1${ showStackedView ? ' btn-small' : '' }` }
            onClick={ () => this.signAndSubmit() }
            disabled={ workflowState.stage > 2 }
          >
            { this.signSubmitButtonText() }
          </Button> }
          { this.showCheckButton() && <Button
            id="check-button"
            type="button"
            className={ `btn btn-primary btn-inline${ isValidatingForm ? ' btn-active' : '' } mt-1${ showStackedView ? ' btn-small' : '' }` }
            disabled={ isBusy || (workflowState.stage >= 2 && !dirty) }
            onClick={ () => document.getElementById('dummy-validate-form-btn').click() }
          >
            <LocalisedMessage id="validationResult.check.buttonText" />
          </Button> }
          { (formDisplayMode !== displayMode.Stacked || showMobileView) && showScrollToTop && <ScrollToTop
            elementId={ showMobileView ? null : 'form-wrapper' }
          /> }
          { showStackedView && <Button
            className={ `btn btn-secondary btn-inline mb-2"${ showStackedView ? ' btn-small' : '' }` }
            onClick={ () => this.onUploadDocumentClick() }
          >
            <LocalisedMessage id="validationResult.uploadDocument" />
          </Button> }
        </div> }
      </React.Fragment>
    );
  }

  setMainContainerClass() {
    const { formDisplayMode } = this.props;
    const { showMobileView } = this.state;

    const mainContainer = document.getElementById('main-container');
    const stackedElement = document.getElementById('stacked-view');
    if (formDisplayMode === displayMode.Stacked && stackedElement && mainContainer && !showMobileView) {
      mainContainer.className = 'container main-content stacked';
    } else {
      mainContainer.className = 'container main-content';
    }
  }

  adjustFormView() {
    this.setState({ showMobileView: window.innerWidth <= FormContainer.maxMobileWidth });
    FormContainer.updateFormDimensions();
    this.setMainContainerClass();
  }

  showScrollToTopIcon(e, elementId) {
    const { showScrollToTop } = this.state;
    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        const scrolled = element.scrollTop !== 0;
        if (showScrollToTop !== scrolled) {
          this.setState({ showScrollToTop: element.scrollTop !== 0 });
        }
      }
    } else {
      const scrolled = window.scrollY !== 0;
      if (showScrollToTop !== scrolled) {
        this.setState({ showScrollToTop: window.scrollY !== 0 });
      }
    }
  }

  signAndSubmit() {
    const { dispatch, match, workflowState } = this.props;
    const { params: { formId } } = match;

    if (workflowState && workflowState.stage === 2) {
      dispatch(setSubmissionStage(submissionStages.Signing));
      return;
    }

    dispatch(submitForm(formId, submissionStages.Confirmation));
  }

  resetSubmisson() {
    const { dispatch } = this.props;
    dispatch(setSubmissionStage(submissionStages.Validating));
  }

  signSubmitButtonText() {
    const { previewOnlyMode } = this.props;
    const messageId = previewOnlyMode ? 'signSubmit.previewOnlyMode.buttonText' : 'signSubmit.buttonText';
    return <LocalisedMessage id={ messageId } />;
  }

  showSignAndSubmit() {
    const { validationResult, dirty } = this.props;
    if (validationResult != null) {
      if (validationResult.rejections == null || validationResult.rejections.length === 0) {
        if (this.formQuestionnaireSupported) {
          return true;
        }
        return !dirty;
      }
    }
    return false;
  }

  showCheckButton() {
    const { validationResult, dirty, isValidatingForm } = this.props;

    return validationResult == null || (validationResult.rejections != null && validationResult.rejections.length > 0) || dirty || isValidatingForm;
  }

  downloadPreviewPdf() {
    const { dispatch, match, intl } = this.props;
    const { params: { formType, formId } } = match;

    dispatch(getPreviewPdf(formId, formType, intl));
  }

  downloadCollectableSupportingDocPreviewPdf(collectableSupportingDocType) {
    const {
      dispatch,
      match,
      intl,
      selectedAccountId,
      validationResult,
      formQuestionnaire
    } = this.props;
    const { params: { formId } } = match;
    dispatch(getCollectableSupportingDocPdf(
      {
        UploadedFormId: formId,
        accountId: selectedAccountId,
        customerName: formQuestionnaire?.answers?.name?.name,
        printName: formQuestionnaire?.answers?.signer?.name,
        customerId: validationResult.customerId,
        collectableSupportingDocType,
        signature: null
      },
      intl
    ));
  }

  renderForm() {
    const { match: { params: { formType } } } = this.props;

    switch (formType) {
      case 'w8bene_2017':
        return this.getBlankComponent(LazyBlankBene2017);
      case 'w8bene_2021':
        return this.getBlankComponent(LazyBlankBene2021);
      case 'w8ben':
        return this.getBlankComponent(LazyBlankBen);
      case 'w8ben_2021':
        return this.getBlankComponent(LazyBlankBen2021);
      case 'w9_2018':
        return this.getBlankComponent(LazyBlankW92018);
      case 'w9_2024':
        return this.getBlankComponent(LazyBlankW92024);
      case 'w8eci':
        return this.getBlankComponent(LazyBlankEci);
      case 'w8eci_2021':
        return this.getBlankComponent(LazyBlankEci2021);
      case 'w8exp':
        return this.getBlankComponent(LazyBlankExp2017);
      case 'w8exp_2023':
        return this.getBlankComponent(LazyBlankExp2023);
      case 'w8imy_2017':
        return this.getBlankComponent(LazyBlankImy2017);
      case 'w8imy_2021':
        return this.getBlankComponent(LazyBlankImy2021);
      case 'crse':
        return this.getBlankComponent(LazyBlankCrsE);
      case 'crsi':
        return this.getBlankComponent(LazyBlankCrsI);
      case 'crsi_2024':
        return this.getBlankComponent(LazyBlankCrsI2024);
      case 'crscp':
        return this.getBlankComponent(LazyBlankCrsCp);
      default:
        return <div>Incorrect blank type</div>;
    }
  }

  render() {
    const { showMobileView, showResult, showSupportingDocs } = this.state;
    const {
      intl,
      match: { params: { formType, formId } },
      isGettingForm,
      workflowState,
      validationResult,
      formError,
      dirty,
      previewOnlyMode,
      formDisplayMode,
      usePreQualifyingQuestionnaire,
      supportingDocs,
      submissionStage,
      isFetchingPdfForm,
      downloadRequestFormType,
      formTypes,
      locale,
      messages,
      englishMessages,
      isFetchingCollectablePdf,
    } = this.props;

    const isNoFormSupported = getFormType(formType)?.isNoFormSupported === true;
    const canViewForm = formTypes.find(x => x.formType === formType && (x.isVisible && x.isEnabled));
    const showStackedView = formDisplayMode === displayMode.Stacked && !showMobileView;
    const formQuestionnaireSupported = usePreQualifyingQuestionnaire && isNoFormSupported;

    const localeToDisplay = formType !== null && isTranslatedForm(formType) && formQuestionnaireSupported
      ? locale
      : defaultLanguage.key;
    const messagesToDisplay = localeToDisplay === defaultLanguage.key ? englishMessages : messages;

    return (
      canViewForm
        ? <IntlProvider key={ localeToDisplay } locale={ localeToDisplay } messages={ messagesToDisplay }>
          { (formError != null && formError.code === 404) && <NotFoundError /> }
          { (formError != null && formError.code !== 404) && <ServerError { ...formError } /> }
          { !isGettingForm && formError == null && formType != null && <React.Fragment>
            { formQuestionnaireSupported && <Suspense fallback={ <div className="loading-container"><LoadingWrapper text={ intl.formatMessage({ id: 'pleaseWaitText' }) } /></div> }>
              <FormQuestionnaire formId={ formId } formType={ formType } onSignSubmit={ () => this.signAndSubmit() } showSupportingDocs={ () => this.onUploadDocumentClick() } /></Suspense>}
            { !formQuestionnaireSupported && <div id={ `${ showStackedView ? 'stacked-view' : 'irs-view' }` } className={ `${ showStackedView ? 'row' : '' }` }>
              <div className={ `${ showStackedView ? 'col-6 no-right-pad' : '' }` }>
                { !isGettingForm && formError == null && formType != null && <section className={ `form ${ showStackedView ? 'stacked' : '' }` }>
                  <div className={ this.getWrapperClasses() }>
                    <div>
                      <div id="form-wrapper" className={ `form-wrapper ${ isGettingForm ? 'loading-form' : '' }` }>
                        <CustomerAvatar className="mobile-username" />
                        <div id="form-content" className="form-content">
                          <div className={ `${ showStackedView ? 'stacked-page' : '' }` }>
                            { (isGettingForm ? <div className="loading-container"><LoadingWrapper text={ intl.formatMessage({ id: 'pleaseWaitText' }) } /></div> : this.renderForm()) }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  { !showStackedView && this.getActionElements() }
                </section> }
                { !showStackedView && <ValidationResults
                  result={ validationResult }
                  showResult={ showResult }
                  dirty={ dirty }
                  onClick={ isOpen => this.setState({
                    showResult: isOpen
                  }) }
                  onSignAndSubmit={ () => this.signAndSubmit() }
                  onUploadDocumentClick={ () => this.onUploadDocumentClick() }
                  previewOnlyMode={ previewOnlyMode }
                /> }
              </div>
              { showStackedView && !isGettingForm && !formError && <div className="col-6 double-left-pad validation-results-stacked-column">
                <ValidationResultsStacked
                  result={ validationResult }
                  showResult={ showResult && showMobileView }
                  dirty={ dirty }
                  onSignAndSubmit={ () => this.signAndSubmit() }
                  previewOnlyMode={ previewOnlyMode }
                  isGettingForm={ workflowState == null }
                />
                { this.getActionElements() }
              </div> }
            </div>}
            { submissionStage === submissionStages.Confirmation && this.showSignAndSubmit() && <FormConfirmation
              onConfirm={ () => this.onFormConfirmationConfirm() }
              onCancel={ () => this.onFormConfirmationCancel() }
              previewOnlyMode={ previewOnlyMode }
              onClickHandler={ () => this.downloadPreviewPdf() }
              isFetchingPdf={ isFetchingPdfForm }
              downloadFormType={ downloadRequestFormType }
              supportingDocOnClickHandler={ supportingDocType => this.downloadCollectableSupportingDocPreviewPdf(supportingDocType) }
              isFetchingCollectablePdf={ isFetchingCollectablePdf }
            /> }
            { submissionStage === submissionStages.Certification && <PenaltiesOfPerjuryModal
              formType={ formType }
              onConfirm={ () => this.onCertificationConfirm() }
              onCancel={ () => this.onCertificationCancel() }
            /> }
            { submissionStage === submissionStages.Signing && <FormSigning
              formType={ formType }
              id={ formId }
              onCancel={ () => this.onFormSigningCancel() }
            /> }
            { showSupportingDocs && <SupportingDocumentsModal
              id={ formId }
              supportingDocs={ supportingDocs }
              onCancel={ () => this.setState({ showSupportingDocs: false }) }
              locale={ localeToDisplay }
            /> }
          </React.Fragment> }
        </IntlProvider>
        : <Redirect to="/" />
    );
  }
}

function getReduxFormDirtyStatus(state, ownProps) {
  let reduxFormName = '';
  if (ownProps != null && ownProps.match != null && ownProps.match.params != null && ownProps.match.params.formType != null) {
    let formName = ownProps.match.params.formType;
    const splitIndex = ownProps.match.params.formType.indexOf('_');
    if (splitIndex > -1) {
      formName = ownProps.match.params.formType.substring(0, splitIndex);
    }
    reduxFormName = `${ formName }-form`;
  }
  return { dirty: isDirty(reduxFormName)(state), reduxFormName };
}

function mapStateToProps(state, ownProps) {
  const {
    app: { onlineConfig: { previewOnlyMode, formDisplayMode, usePreQualifyingQuestionnaire, authenticationType, customer: { isTemporaryCustomer }, customerEmailAddress, defaultFormType, formTypes }, locale, messages, englishMessages },
    authentaLink: { authenticationPinVerified, showPinVerification },
    form: {
      isValidatingForm,
      validationResult,
      supportingDocs,
      workflowState,
      isGettingForm,
      formData,
      formError,
      instructionToggled,
      isFormConfirmationShown,
      submissionStage,
      pdfData,
      isFetchingPdfForm,
      downloadRequestFormType,
      selectedAccountId
    },
    supportingDocuments: {
      isFetchingCollectablePdf,
      collectableSupportingDocPdfData
    },
    questionnaires: {
      formQuestionnaire
    }
  } = state;

  const { dirty, reduxFormName } = getReduxFormDirtyStatus(state, ownProps);

  return {
    isValidatingForm,
    validationResult,
    supportingDocs,
    workflowState,
    isGettingForm,
    formError,
    formData,
    instructionToggled,
    dirty,
    reduxFormName,
    isFormConfirmationShown,
    submissionStage,
    pdfData,
    isFetchingPdfForm,
    downloadRequestFormType,
    previewOnlyMode,
    formDisplayMode,
    usePreQualifyingQuestionnaire,
    authenticationType,
    isTemporaryCustomer,
    authenticationPinVerified,
    showPinVerification,
    emailAddress: customerEmailAddress,
    defaultFormType,
    formTypes,
    locale,
    messages,
    englishMessages,
    isFetchingCollectablePdf,
    collectableSupportingDocPdfData,
    selectedAccountId,
    formQuestionnaire
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(FormContainer));
